import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API } from '../lib/api';

const CheckoutSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading');
    const [paymentData, setPaymentData] = useState(null);
    const { clearCart } = useCart();
    const sessionId = searchParams.get('session_id');

    useEffect(() => {
        const pollStatus = async (attempts = 0) => {
            const maxAttempts = 5;
            const pollInterval = 2000;

            if (attempts >= maxAttempts) {
                setStatus('timeout');
                return;
            }

            try {
                const response = await axios.get(`${API}/checkout/status/${sessionId}`);
                setPaymentData(response.data);

                if (response.data.payment_status === 'paid') {
                    setStatus('success');
                    clearCart();
                    return;
                } else if (response.data.status === 'expired') {
                    setStatus('expired');
                    return;
                }

                // Continue polling
                setTimeout(() => pollStatus(attempts + 1), pollInterval);
            } catch (error) {
                console.error('Error checking payment status:', error);
                setStatus('error');
            }
        };

        if (sessionId) {
            pollStatus();
        } else {
            setStatus('error');
        }
    }, [sessionId, clearCart]);

    return (
        <div className="min-h-screen bg-white" data-testid="checkout-success-page">
            <SEO
                title="Order Confirmed"
                description="Thank you for your Wayside Maple purchase! Your order has been confirmed and is being prepared."
                path="/checkout/success"
            />
            <Header />
            
            <section className="pt-32 pb-24 min-h-[80vh] flex items-center">
                <div className="max-w-xl mx-auto px-6 text-center">
                    {status === 'loading' && (
                        <div data-testid="checkout-loading">
                            <Loader2 className="w-16 h-16 text-maple-red mx-auto mb-6 animate-spin" />
                            <h1 className="font-anton text-4xl text-maple-dark mb-4">
                                PROCESSING PAYMENT
                            </h1>
                            <p className="text-maple-dark/60">Please wait while we confirm your order...</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div data-testid="checkout-success">
                            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                            <h1 className="font-anton text-4xl text-maple-dark mb-4">
                                ORDER CONFIRMED
                            </h1>
                            <p className="text-maple-dark/60 mb-2">Thank you for your purchase!</p>
                            {paymentData && (
                                <p className="font-mono text-maple-red text-lg mb-8">
                                    Total: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                                </p>
                            )}
                            <Link to="/shop" className="btn-primary inline-block">
                                <span>Continue Shopping</span>
                            </Link>
                        </div>
                    )}

                    {status === 'expired' && (
                        <div data-testid="checkout-expired">
                            <XCircle className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
                            <h1 className="font-anton text-4xl text-maple-dark mb-4">
                                SESSION EXPIRED
                            </h1>
                            <p className="text-maple-dark/60 mb-8">Your payment session has expired. Please try again.</p>
                            <Link to="/cart" className="btn-primary inline-block">
                                <span>Return to Cart</span>
                            </Link>
                        </div>
                    )}

                    {(status === 'error' || status === 'timeout') && (
                        <div data-testid="checkout-error">
                            <XCircle className="w-20 h-20 text-maple-red mx-auto mb-6" />
                            <h1 className="font-anton text-4xl text-maple-dark mb-4">
                                SOMETHING WENT WRONG
                            </h1>
                            <p className="text-maple-dark/60 mb-8">
                                We couldn't verify your payment. Please check your email for confirmation 
                                or contact us if you need assistance.
                            </p>
                            <div className="flex gap-4 justify-center">
                                <Link to="/cart" className="btn-secondary">
                                    Return to Cart
                                </Link>
                                <Link to="/contact" className="btn-primary">
                                    <span>Contact Us</span>
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default CheckoutSuccessPage;
